// React Query hooks for per-user notification preferences (GET/PUT /profile/notifications).
// Mirrors useProfile.ts in structure and cache-key convention.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '../services/api';
import type { UpdateNotificationPreferences } from '../types';

/** Query key for notification preferences — scoped under the 'profile' namespace. */
const NOTIFICATION_PREFS_KEY = ['profile', 'notifications'] as const;

/** Fetch the authenticated user's notification preferences. */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: NOTIFICATION_PREFS_KEY,
    queryFn: () => profileApi.getNotifications(),
  });
}

/** Mutate the authenticated user's notification preferences.
 *  On success, invalidates the query so any mounted consumers re-fetch the
 *  latest server state rather than showing stale toggle positions. */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateNotificationPreferences) =>
      profileApi.updateNotifications(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_PREFS_KEY });
    },
  });
}
